import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  hydrateRepliesFromCache,
  fetchCommentReplies,
  createReply,
  updateReply,
  removeReply
} from "./thunks.js";
import { toggleReplyLikeAsync } from "../commentLikesSlice.js";

/** Keep in-flight optimistic/temp replies when a stale fetch completes. */
const mergeRepliesPreservingOptimistic = (serverReplies, existingList = []) => {
  const merged = Array.isArray(serverReplies) ? [...serverReplies] : [];
  const serverIds = new Set(merged.map((r) => r.id));
  for (const reply of existingList) {
    const isOptimistic =
      reply?.isOptimistic ||
      (typeof reply?.id === "string" && reply.id.startsWith("temp-reply-"));
    if (isOptimistic && !serverIds.has(reply.id)) {
      merged.push(reply);
    }
  }
  return merged;
};

const initialState = {
  items: {}, // { commentId: [replies] }
  loading: {}, // { commentId: boolean }
  errors: {}, // { commentId: error }
  rollback: {}, // { commentId: { [replyId]: replyObj } }
  editRollback: {}, // { [replyId]: previousText }
};

const repliesSlice = createSlice({
  name: "replies",
  initialState,
  reducers: {
    patchReplyLikeCount: (state, action) => {
      const { commentId, replyId, likes } = action.payload || {};
      if (!commentId || !replyId || typeof likes !== "number") return;
      const list = state.items[commentId];
      if (!list) return;
      const reply = list.find((r) => r.id === replyId);
      if (reply) reply.likes = likes;
    },
    clearReplies: (state) => {
      state.items = {};
      state.loading = {};
      state.errors = {};
    },
    clearReplyError: (state, action) => {
      const { commentId } = action.payload;
      delete state.errors[commentId];
    },
    setReplyLoading: (state, action) => {
      const { commentId, isLoading } = action.payload;
      state.loading[commentId] = isLoading;
    },
  },
  extraReducers: (builder) => {
    builder
      // Hydrate from cache
      .addCase(hydrateRepliesFromCache.fulfilled, (state, action) => {
        const { commentId, replies } = action.payload;
        if (!state.items[commentId]) {
          state.items[commentId] = replies;
        }
      })

      // Fetch replies
      .addCase(fetchCommentReplies.pending, (state, action) => {
        const { commentId } = action.meta.arg;
        state.loading[commentId] = true;
        delete state.errors[commentId];
      })
      .addCase(fetchCommentReplies.fulfilled, (state, action) => {
        const { commentId, replies } = action.payload;
        state.items[commentId] = mergeRepliesPreservingOptimistic(
          replies,
          state.items[commentId],
        );
        state.loading[commentId] = false;
      })
      .addCase(fetchCommentReplies.rejected, (state, action) => {
        const { commentId } = action.meta.arg;
        state.loading[commentId] = false;
        state.errors[commentId] = action.payload || "Failed to fetch replies";
      })

      // Create reply (optimistic)
      .addCase(createReply.pending, (state, action) => {
        const { commentId, replyData, tempId } = action.meta.arg;
        const optimistic = {
          ...replyData,
          id: tempId,
          isOptimistic: true,
          timestamp: new Date().toISOString(),
          _commentId: commentId,
        };
        if (!state.items[commentId]) state.items[commentId] = [];
        state.items[commentId].push(optimistic);
        state.loading[commentId] = true;
      })
      .addCase(createReply.fulfilled, (state, action) => {
        const { commentId, tempId, reply } = action.payload;
        const list = state.items[commentId] || [];
        const idx = list.findIndex((r) => r.id === tempId || r.id === reply.id);
        if (idx >= 0) {
          list[idx] = { ...reply, isOptimistic: false };
        } else {
          list.push(reply);
        }
        state.loading[commentId] = false;
      })
      .addCase(createReply.rejected, (state, action) => {
        const { commentId, tempId } = action.meta.arg;
        if (state.items[commentId]) {
          state.items[commentId] = state.items[commentId].filter(
            (r) => r.id !== tempId,
          );
        }
        state.loading[commentId] = false;
        state.errors[commentId] = action.payload || "Failed to create reply";
        toast.error(state.errors[commentId]);
      })

      // Update reply
      .addCase(updateReply.pending, (state, action) => {
        const { commentId, replyId, text } = action.meta.arg;
        const list = state.items[commentId] || [];
        const r = list.find((x) => x.id === replyId);
        if (r) {
          state.editRollback[replyId] = r.text;
          r.text = text;
        }
      })
      .addCase(updateReply.fulfilled, (state, action) => {
        const { commentId, replyId } = action.payload;
        delete state.editRollback[replyId];
        const list = state.items[commentId] || [];
        const r = list.find((x) => x.id === replyId);
        if (r) {
          r.updatedAt = new Date().toISOString();
        }
      })
      .addCase(updateReply.rejected, (state, action) => {
        const { commentId, replyId } = action.meta.arg;
        const list = state.items[commentId] || [];
        const r = list.find((x) => x.id === replyId);
        if (r && state.editRollback[replyId]) {
          r.text = state.editRollback[replyId];
        }
        delete state.editRollback[replyId];
        toast.error(action.payload || "Failed to update reply");
      })

      // Remove reply (soft delete)
      .addCase(removeReply.pending, (state, action) => {
        const { replyId, commentId } = action.meta.arg;
        const list = state.items[commentId] || [];
        const r = list.find((x) => x.id === replyId);
        if (r) {
          if (!state.rollback[commentId]) state.rollback[commentId] = {};
          // Snapshot before mutation (BUG 2)
          state.rollback[commentId][replyId] = {
            text: r.text,
            deletedAt: r.deletedAt,
            postId: r.postId,
            _postId: r._postId,
          };
          r.deletedAt = new Date().toISOString();
          r.text = "[Reply removed]";
        }
      })
      .addCase(removeReply.fulfilled, (state, action) => {
        const { replyId, commentId } = action.payload;
        // Clean up rollback after some time (or keep for undo)
        if (state.rollback[commentId]?.[replyId]) {
          delete state.rollback[commentId][replyId];
        }
      })
      .addCase(removeReply.rejected, (state, action) => {
        const { replyId, commentId } = action.meta.arg;
        const list = state.items[commentId] || [];
        const r = list.find((x) => x.id === replyId);
        const rollback = state.rollback[commentId]?.[replyId];
        if (r && rollback) {
          r.text = rollback.text;
          // BUG 2 fix: if original had no deletedAt, delete the key rather than assigning undefined
          if (rollback.deletedAt !== undefined) {
            r.deletedAt = rollback.deletedAt;
          } else {
            delete r.deletedAt;
          }
        }
        if (state.rollback[commentId]?.[replyId]) {
          delete state.rollback[commentId][replyId];
        }
        toast.error(action.payload || "Failed to delete reply");
      })

      // Cross-slice sync: when a reply like is toggled, update the reply's likes count
      // and isLiked flag in state.replies.items[commentId] so the UI heart icon and
      // count display update without a full re-fetch.
      .addCase(toggleReplyLikeAsync.fulfilled, (state, action) => {
        const { commentId, replyId, isLiked, likes } = action.payload;
        if (!commentId || !replyId || typeof likes !== "number") return;
        const list = state.items[commentId];
        if (!list) return;
        const reply = list.find((r) => r.id === replyId);
        if (reply) {
          reply.likes = likes;
          reply.isLiked = isLiked;
        }
      });
  },
});

export const { clearReplies, clearReplyError, setReplyLoading, patchReplyLikeCount } =
  repliesSlice.actions;

export default repliesSlice.reducer;
